// ============================================
// pages/unloading/UnloadingDashboard.tsx — Real dashboard (client-side rollup)
// Reads live from unloading_entries, unloading_bills.
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, PackageOpen, DollarSign, AlertTriangle, ArrowUpRight, Wallet, Receipt,
} from 'lucide-react';
import {
  seasonsAPI, unloadingEntriesAPI, unloadingBillsAPI, unloadingContractorsAPI,
} from '../../api/services';
import { formatAmount, num } from '../../types/markfed';
import type { Season } from '../../types/markfed';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-700',
  submitted: 'bg-blue-100 text-blue-700',
  dm_verified: 'bg-orange-100 text-orange-700',
  warehouse_certified: 'bg-blue-100 text-blue-700',
  manager_approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export const UnloadingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
  const [entries, setEntries] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([seasonsAPI.list(), unloadingContractorsAPI.list()]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setContractors(c.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (active) setSelectedSeasonId(active.id);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedSeasonId) return;
    (async () => {
      setLoading(true);
      try {
        const [e, b] = await Promise.all([
          unloadingEntriesAPI.list(selectedSeasonId),
          unloadingBillsAPI.list(selectedSeasonId),
        ]);
        setEntries(Array.isArray(e.data) ? e.data : []);
        setBills(Array.isArray(b.data) ? b.data : []);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load season data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedSeasonId]);

  const entryStats = useMemo(() => ({
    total: entries.length,
    approved: entries.filter((e) => e.status === 'manager_approved').length,
    pending: entries.filter((e) => ['submitted', 'dm_verified'].includes(e.status)).length,
    draft: entries.filter((e) => (e.status || 'draft') === 'draft').length,
    rejected: entries.filter((e) => e.status === 'rejected').length,
    total_bags: entries.reduce((s, e) => s + num(e.bags_unloaded), 0),
    total_weight: entries.reduce((s, e) => s + num(e.weight_qtl), 0),
    total_claimed: entries.reduce((s, e) => s + num(e.amount_claimed), 0),
    total_verified: entries.reduce((s, e) => s + num(e.amount_verified), 0),
  }), [entries]);

  const billStats = useMemo(() => ({
    total: bills.length,
    draft: bills.filter((b) => (b.status || 'draft') === 'draft').length,
    approved: bills.filter((b) => b.status === 'manager_approved').length,
    pending: bills.filter((b) => ['warehouse_certified', 'dm_verified'].includes(b.status)).length,
    total_claimed: bills.reduce((s, b) => s + (num(b.total_amount_claimed_agg) || num(b.total_bill_claimed)), 0),
    total_verified: bills.reduce((s, b) => s + (num(b.total_amount_verified_agg) || num(b.mgr_total) || num(b.dm_total)), 0),
    total_paid: bills.reduce((s, b) => s + num(b.payment_released), 0),
    total_tds: bills.reduce((s, b) => s + num(b.tds_amount), 0),
  }), [bills]);

  const balanceOutstanding = billStats.total_verified - billStats.total_paid - billStats.total_tds;
  const settlementPct = billStats.total_verified > 0
    ? +((billStats.total_paid / billStats.total_verified) * 100).toFixed(1)
    : 0;

  const topContractors = useMemo(() => {
    const map: Record<number, { id: number; name: string; bills: number; claimed: number; verified: number; paid: number }> = {};
    for (const c of contractors) {
      map[c.id] = { id: c.id, name: c.firm_name, bills: 0, claimed: 0, verified: 0, paid: 0 };
    }
    for (const b of bills) {
      const r = map[b.contractor_id];
      if (!r) continue;
      r.bills++;
      r.claimed += num(b.total_amount_claimed_agg) || num(b.total_bill_claimed);
      r.verified += num(b.total_amount_verified_agg) || num(b.mgr_total) || num(b.dm_total);
      r.paid += num(b.payment_released);
    }
    return Object.values(map)
      .filter((r) => r.bills > 0)
      .sort((a, b) => b.verified - a.verified)
      .slice(0, 5);
  }, [contractors, bills]);

  const recentBills = useMemo(() =>
    [...bills]
      .sort((a, b) => (b.bill_date || '').localeCompare(a.bill_date || ''))
      .slice(0, 5)
  , [bills]);

  const unpaidOnApproved = bills.filter((b) =>
    b.status === 'manager_approved' && num(b.payment_released) < (num(b.total_amount_verified_agg) || num(b.mgr_total))
  ).length;

  if (loading && entries.length === 0 && bills.length === 0) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <PackageOpen className="w-7 h-7 text-blue-600" />
            Unloading Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Real-time rollup across entries, bills, and contractors for the selected season.</p>
        </div>
        <select value={selectedSeasonId} onChange={(e) => setSelectedSeasonId(parseInt(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.season_name}</option>)}
        </select>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}

      {/* Top-row headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 uppercase">Total Entries</p>
            <PackageOpen className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 mt-1">{entryStats.total.toLocaleString('en-IN')}</p>
          <p className="text-[11px] text-gray-500 mt-1">
            {entryStats.approved} approved · {entryStats.pending} pending · {entryStats.draft} draft
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 uppercase">Total Bills</p>
            <Receipt className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-gray-900 mt-1">{billStats.total.toLocaleString('en-IN')}</p>
          <p className="text-[11px] text-gray-500 mt-1">
            {billStats.approved} approved · {billStats.pending} pending
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 uppercase">Amount Verified</p>
            <DollarSign className="w-4 h-4 text-gray-400" />
          </div>
          <p className="text-3xl font-bold text-blue-600 mt-1">{formatAmount(billStats.total_verified)}</p>
          <p className="text-[11px] text-gray-500 mt-1">Claimed {formatAmount(billStats.total_claimed)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 uppercase">Balance to Release</p>
            <Wallet className="w-4 h-4 text-gray-400" />
          </div>
          <p className={`text-3xl font-bold mt-1 ${balanceOutstanding > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {formatAmount(balanceOutstanding)}
          </p>
          <p className="text-[11px] text-gray-500 mt-1">
            Paid {formatAmount(billStats.total_paid)} · {settlementPct}% settled
          </p>
        </div>
      </div>

      {/* Second row: volume + alerts */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs text-gray-600 uppercase">Volume — Entries</p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <p className="text-[10px] text-gray-500">Total Bags</p>
              <p className="text-xl font-bold text-gray-900">{entryStats.total_bags.toLocaleString('en-IN')}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500">Weight (Qtl)</p>
              <p className="text-xl font-bold text-gray-900">{entryStats.total_weight.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase">Tax Withheld</p>
          <p className="text-2xl font-bold text-red-600 mt-2">{formatAmount(billStats.total_tds)}</p>
          <p className="text-[10px] text-gray-400">Sec 194C on verified bills</p>
        </div>
        <div className={`rounded-xl p-4 border ${unpaidOnApproved > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase text-gray-700">Approved w/ Balance</p>
            <AlertTriangle className={`w-4 h-4 ${unpaidOnApproved > 0 ? 'text-orange-600' : 'text-green-600'}`} />
          </div>
          <p className={`text-2xl font-bold mt-1 ${unpaidOnApproved > 0 ? 'text-orange-700' : 'text-green-700'}`}>
            {unpaidOnApproved}
          </p>
          <p className="text-[10px] text-gray-500">Bills approved but not fully paid</p>
        </div>
      </div>

      {/* Top contractors + Recent bills */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">Top Contractors by Verified Amount</h3>
            <button onClick={() => navigate('/unloading/payments')}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
              View all <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          {topContractors.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No contractor activity yet</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-500 uppercase text-[10px] border-b">
                  <th className="text-left py-2">Firm</th>
                  <th className="text-right py-2">Bills</th>
                  <th className="text-right py-2">Verified</th>
                  <th className="text-right py-2">Paid</th>
                  <th className="text-right py-2">%</th>
                </tr>
              </thead>
              <tbody>
                {topContractors.map((c) => {
                  const pct = c.verified > 0 ? (c.paid / c.verified) * 100 : 0;
                  return (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="py-2 text-gray-900 font-medium">{c.name}</td>
                      <td className="py-2 text-right text-gray-700">{c.bills}</td>
                      <td className="py-2 text-right text-gray-900 font-medium">{formatAmount(c.verified)}</td>
                      <td className="py-2 text-right text-green-700">{formatAmount(c.paid)}</td>
                      <td className="py-2 text-right text-gray-700">{pct.toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">Recent Bills</h3>
            <button onClick={() => navigate('/unloading')}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
              View all <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          {recentBills.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No bills yet. <button onClick={() => navigate('/unloading/bills/new')} className="text-blue-600 hover:underline">Create one</button></p>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentBills.map((b) => (
                <div key={b.id} className="py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
                  onClick={() => navigate(`/unloading/bills/${b.id}`)}>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{b.invoice_no || `Bill #${b.id}`}</p>
                    <p className="text-[11px] text-gray-500">{b.bill_date || '—'} · {b.godown?.godown_name || `Godown #${b.godown_id}`}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatAmount(num(b.total_amount_verified_agg) || num(b.mgr_total) || num(b.total_bill_claimed))}
                    </p>
                    <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                      {(b.status || 'draft').replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate('/unloading/entries/new')}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
            + New Entry
          </button>
          <button onClick={() => navigate('/unloading/bills/new')}
            className="px-4 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg">
            + New Bill
          </button>
          <button onClick={() => navigate('/unloading/entries')}
            className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg">
            All Entries
          </button>
          <button onClick={() => navigate('/unloading')}
            className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg">
            All Bills
          </button>
          <button onClick={() => navigate('/unloading/payments')}
            className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg">
            Payments Ledger
          </button>
          <button onClick={() => navigate('/admin/unloading-contractors')}
            className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-lg">
            Manage Contractors
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnloadingDashboard;
